import compact from 'lodash/compact';
import { Popover } from 'antd';

import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import ConnectivityIcon from '@/comp/MapBuilder/components/ConnectivityIcon/ConnectivityIcon';
import FlowIcon from '@/comp/common/icons/FlowIcon';
import SensorWidgetWithIcon from '@/comp/common/icons/SensorWidgetWithIcon';
import { token } from '@/theme';
import { getWidgetCircleColor } from '@/comp/MapBuilder/components/SensorNodeWidget/SensorNodeWidget.utils';
import { DeviceConnectivity } from '@/constants/enums';
import PressureIcon from '@/comp/common/icons/PressureIcon';
import DewpointIcon from '@/comp/common/icons/DewPointIcon';
import AirTemperatureIcon from '@/comp/common/icons/AirTemperatureIcon';
import ComboSensorWidgetWithIcon from '@/comp/common/icons/ComboSensorWidgetWithIcon';
import WaterPressureIcon from '@/comp/common/icons/WaterPressureIcon';
import PressureComboIcon from '@/comp/common/icons/PressureComboIcon';
import DissolvedOxygenIcon from '@/comp/common/icons/DissolvedOxygenIcon';
import InfluentFlowIcon from '@/comp/common/icons/InfluentFlowIcon';
import WidgetWithIcon from '@/comp/common/icons/WidgetWithIcon';

import AdvancedInfo from './Tabs/AdvancedInfo/AdvancedInfo';
import AssetInfo from './Tabs/AssetInfo/AssetInfo';
import { Entity } from '../NodeWidget.types';
import AssetImages from './Tabs/AssetImages/AssetImages';
import ConnectivityLight from '../../ConnectivityLight';
import { HelixConnectivityEnum } from '@/comp/MapBuilder/components/HelixConnectivityIcon/HelixConnectivityIcon.utils';

export const getTabItems = (entity, t, isInEditMode) => [
  {
    label: t('assetInfo'),
    key: 'assetInfo',
    children: <AssetInfo entity={entity} />,
  },
  ...(compressorTypes.slice(0, -1).includes(entity?.deviceType as DeviceType)
    ? [
        {
          label: t('advancedInfo'),
          key: 'advancedInfo',
          children: <AdvancedInfo entity={entity} />,
        },
      ]
    : []),
  ...(!isInEditMode
    ? [
        {
          label: t('assetImages'),
          key: 'assetImages',
          children: <AssetImages deviceId={entity?.deviceId} />,
        },
      ]
    : []),
];

export const getAssetNameDetails = entity =>
  compact([entity?.brand, entity?.modelDisplayName || entity?.model, entity?.deviceControllerName]).join(' | ');

export const shouldShowFooter = (entity: Entity, fromSCC: boolean) =>
  entity?.deviceType === DeviceType.Booster || (fromSCC && compressorTypes.includes(entity?.deviceType as DeviceType));

export const shouldShowMinimizeButton = (isMobile: boolean, fromSCC: boolean, isInEditMode: boolean) =>
  !isMobile && !fromSCC && !isInEditMode;

const IconMapping = {
  [DeviceType.FlowSensor]: [FlowIcon],
  [DeviceType.PressureSensor]: [PressureIcon],
  [DeviceType.DewpointSensor]: [DewpointIcon],
  [DeviceType.AirTemperatureSensor]: [AirTemperatureIcon],
  [DeviceType.WaterPressureSensor]: [WaterPressureIcon],
  [DeviceType.DewpointAndPressureSensor]: [DewpointIcon, PressureComboIcon],
  [DeviceType.FlowAndPressureSensor]: [FlowIcon, PressureComboIcon],
};

export const getConnectivityIcon = (deviceType, entity, currentAsset, t) => {
  if (!entity) {
    return null;
  }

  const { connectivity, controlMode, messages } = entity;
  const isConnected = connectivity === DeviceConnectivity.Connected;
  const isCompressor = compressorTypes.includes(deviceType);
  const [FirstIcon, SecondIcon] = IconMapping[deviceType] ?? [];
  const getConnectivityIconForGenericAsset = (connectivity: string) => {
    const mapping = {
      disconnected: HelixConnectivityEnum.Disconnected,
      noState: HelixConnectivityEnum.Null,
    };
    return mapping[connectivity] || connectivity;
  };

  switch (deviceType) {
    case DeviceType.FlowSensor:
    case DeviceType.PressureSensor:
    case DeviceType.DewpointSensor:
    case DeviceType.AirTemperatureSensor:
    case DeviceType.WaterPressureSensor:
      return (
        <Popover content={t(connectivity)}>
          <SensorWidgetWithIcon
            Icon={<FirstIcon />}
            circleColor={getWidgetCircleColor(isConnected, token)}
            backgroundColor={token.colorBgElevated}
            width={32}
            height={32}
          />
        </Popover>
      );
    case DeviceType.DewpointAndPressureSensor:
    case DeviceType.FlowAndPressureSensor:
      return (
        <Popover content={t(connectivity)}>
          <ComboSensorWidgetWithIcon
            Icon1={<FirstIcon />}
            Icon2={<SecondIcon />}
            circleColor={getWidgetCircleColor(isConnected, token)}
            backgroundColor={token.colorBgElevated}
          />
        </Popover>
      );
    case DeviceType.DryAirTank:
    case DeviceType.WetAirTank:
      return null;
    case DeviceType.DissolvedOxygenSensor:
      return (
        <Popover content={t(connectivity)}>
          <WidgetWithIcon
            width={32}
            height={32}
            isSelected={false}
            borderColor={token.colorBorder}
            widgetFillColor={token.colorBgElevated}
            ConnectivityIcon={
              <ConnectivityLight
                connectivity={connectivity}
                inMaintenance={false}
                Icon={DissolvedOxygenIcon}
                token={token}
              />
            }
          />
        </Popover>
      );
    case DeviceType.InfluentFlowSensor:
      return (
        <Popover content={t(connectivity)}>
          <WidgetWithIcon
            width={32}
            height={32}
            isSelected={false}
            borderColor={token.colorBorder}
            widgetFillColor={token.colorBgElevated}
            ConnectivityIcon={
              <ConnectivityLight
                connectivity={connectivity}
                inMaintenance={false}
                Icon={InfluentFlowIcon}
                token={token}
              />
            }
          />
        </Popover>
      );
    case DeviceType.General:
    case DeviceType.Pump:
    case DeviceType.Vacuum:
      return (
        <ConnectivityIcon
          connectivity={connectivity}
          isCompressor={isCompressor}
          helixConnectivityStatus={getConnectivityIconForGenericAsset(connectivity)}
          controlMode={controlMode}
          messages={messages}
          width={32}
          height={32}
        />
      );
    default:
      return (
        <ConnectivityIcon
          connectivity={connectivity}
          isCompressor={isCompressor}
          helixConnectivityStatus={currentAsset?.helixConnectivityStatusC}
          controlMode={controlMode}
          messages={messages}
          width={32}
          height={32}
        />
      );
  }
};
